import type { Project } from "@szyos/contracts";
import { adoptDone } from "@szyos/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { api, errorMessage } from "../api";
import { Icon } from "../icons";
import { useToast } from "./feedback";
import { Markdown } from "./Markdown";

/**
 * Ergebnis der Bestandsaufnahme im Projekt (UX-11): Empfehlung, nächster Schritt als Auftrag mit einem Tipp,
 * „Fertig heißt“ und „Später“ übernehmen, die ganze Datei zum Nachlesen.
 */
export function StocktakePanel({ project }: { project: Project }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const view = useQuery({ queryKey: ["stocktake", project.id], queryFn: () => api.stocktake(project.id) });
  const doc = view.data?.doc ?? null;
  const workOrders = useQuery({ queryKey: ["work-orders"], queryFn: api.workOrders });
  // Schon angelegt, wenn es im Projekt einen Auftrag mit diesem Titel gibt; dann kein zweites Angebot.
  const orderExists = (workOrders.data ?? []).some(
    (w) => w.projectId === project.id && doc?.nextStep && w.title.trim() === doc.nextStep.title.trim(),
  );

  const order = useMutation({
    mutationFn: () => api.orderFromStocktake(project.id),
    onSuccess: async (wo) => {
      await queryClient.invalidateQueries({ queryKey: ["work-orders"] });
      toast("Auftrag mit fertigem Briefing angelegt. Prüfe es und gib es frei.");
      void navigate({ to: "/work-orders/$orderId", params: { orderId: wo.id } });
    },
    onError: (err) => toast(errorMessage(err), "error"),
  });

  const adopted = adoptDone(
    project.doneCriteria.map(({ text, met }) => ({ text, met })),
    project.later,
    doc ?? { done: [], later: [] },
  );
  // Übernommen, wenn das Zusammenführen nichts mehr ändern würde.
  const alreadyAdopted =
    adopted.criteria.length === project.doneCriteria.length && adopted.later.length === project.later.length;
  const adopt = useMutation({
    mutationFn: () => api.setProjectDone(project.id, adopted),
    onSuccess: async (p) => {
      queryClient.setQueryData(["project", project.id], p);
      await queryClient.invalidateQueries({ queryKey: ["projects"] });
      toast("„Fertig heißt“ und „Später“ aus der Bestandsaufnahme übernommen.");
    },
    onError: (err) => toast(errorMessage(err), "error"),
  });

  if (view.isLoading) return null;
  const data = view.data;
  // Ohne jede Bestandsaufnahme bleibt das Projekt aufgeräumt; der Knopf zum Anlegen steht weiter unten.
  if (!data || (!data.orderId && !data.doc)) return null;

  return (
    <details className="panel fold stocktake-panel" id="bestandsaufnahme">
      <summary>
        <h2>Bestandsaufnahme</h2> <span className="muted">Empfehlung und Vorschläge aus dem Repo</span>
      </summary>
      {!doc ? (
        <p className="muted" role="status">
          {data.reason}
        </p>
      ) : (
        <>
          <p className="muted">
            {data.source === "branch" ? "Aus dem Pull Request, noch nicht gemergt." : "Aus dem Repo."}{" "}
            {data.prUrl && (
              <a className="link" href={data.prUrl} target="_blank" rel="noreferrer noopener">
                Pull Request ansehen
              </a>
            )}
          </p>
          {doc.recommendation && (
            <p className="stocktake-rec">
              <strong>Empfehlung:</strong> {doc.recommendation}
            </p>
          )}

          {doc.nextStep && (
            <div className="stocktake-block">
              <h3>Nächster Schritt</h3>
              <p>
                <strong>{doc.nextStep.title}</strong>
              </p>
              {doc.nextStep.why && <p className="muted">Warum: {doc.nextStep.why}</p>}
              {doc.nextStep.tasks.length > 0 && (
                <ol>
                  {doc.nextStep.tasks.map((t, i) => (
                    <li key={`${i}-${t}`}>{t}</li>
                  ))}
                </ol>
              )}
              {orderExists ? (
                <p className="muted">
                  <Icon name="check" /> Als Auftrag angelegt, siehe „Aufträge“.
                </p>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn btn--primary"
                    disabled={order.isPending}
                    onClick={() => order.mutate()}
                  >
                    <span className="btn-label">
                      <Icon name="plus" /> {order.isPending ? "Wird angelegt …" : "Als Auftrag anlegen"}
                    </span>
                  </button>
                  <p className="muted small">
                    Das Briefing ist fertig ausgefüllt; du gibst es nur noch frei.
                  </p>
                </>
              )}
            </div>
          )}

          {doc.done.length > 0 && (
            <div className="stocktake-block">
              <h3>Fertig heißt (Vorschlag)</h3>
              <ol>
                {doc.done.map((t) => (
                  <li key={t}>{t}</li>
                ))}
              </ol>
              {doc.later.length > 0 && <p className="muted">Dazu {doc.later.length} Punkte für „Später“.</p>}
              <button
                type="button"
                className={alreadyAdopted ? "btn btn--done" : "btn"}
                disabled={adopt.isPending || alreadyAdopted}
                onClick={() => adopt.mutate()}
              >
                <span className="btn-label">
                  <Icon name="check" />{" "}
                  {alreadyAdopted ? "Ins Projekt übernommen" : "In „Fertig heißt“ und „Später“ übernehmen"}
                </span>
              </button>
              {!alreadyAdopted && project.doneCriteria.length > 0 && (
                <p className="muted small">
                  Vorhandene Kriterien bleiben. Neue kommen dazu, bis sieben voll sind; der Rest geht nach
                  „Später“.
                </p>
              )}
            </div>
          )}

          {data.markdown && (
            <details className="stocktake-full">
              <summary>Ganze Bestandsaufnahme lesen</summary>
              <Markdown text={data.markdown} />
            </details>
          )}
        </>
      )}
    </details>
  );
}
